import type { HttpErrorOptions } from './errors';
import {
  NetworkError,
  ResponseDecodeError,
  ResponseTooLargeError,
} from './errors';

/**
 * The failures a shared flight can raise, and therefore the ones that have to be
 * rebuilt per participant.
 *
 * @remarks
 * A VALUE and not a chain of `instanceof` branches, so that a test can read the
 * set and hold it to what the shared region actually throws. As a chain it was a
 * fact about two files that nothing compared; `shared-failures.test.ts` walks
 * this array and fails on a member it has no scenario for.
 *
 * `failure.constructor` would be the generic alternative and is not available:
 * MEASURED, it needs a narrowing assertion before it can be called and this
 * package's `oxlint` gate refuses those. Reading the class off a list this module
 * owns is neither a dispatch on attacker-controlled data nor an assertion.
 *
 * The three share one signature, `(method, url, options?)`. Their two base
 * classes take four arguments, so a rebuild that guessed at the shape would
 * shift the arguments of anything outside this list.
 *
 * It held five until the abort classification moved out of the shared region
 * (`CO64`). `RequestTimeoutError` and `RequestAbortError` were only ever raised
 * in there by that classification, so once it left, those two branches could not
 * be reached by anything: the transport's own throws arrive relabelled as
 * `NetworkError`, unconditionally. Keeping them would have suggested a cover
 * that was not there.
 */
export const REBUILDABLE = [
  NetworkError,
  ResponseDecodeError,
  ResponseTooLargeError,
] as const;

/**
 * Gives one participant of a shared flight a failure it can call its own.
 *
 * @remarks
 * A rejected promise hands every `await` on it the SAME object. MEASURED, and it
 * is the whole reason this exists: without it two callers who joined one flight
 * read one `callId`, one duration and one set of options, and at most one of the
 * three is true for either of them (`CO59`).
 *
 * Rebuilt rather than cloned. MEASURED: copying through property descriptors
 * loses the stack, because V8 installs it as an accessor on the instance, so the
 * copy points at wherever the copy was made.
 *
 * The `cause` travels along, so what actually went wrong is the same fact on
 * every participant's copy. Only the attribution differs.
 *
 * Anything outside the list goes through UNCHANGED (`CO61`), which fails towards
 * the behavior that was already there. That is not free, and the comment here
 * used to claim it was: such a failure keeps the OPENER's `method` and `url`,
 * and a named pool (`CO3`) lets those differ from the joiner's. The guard on
 * `REBUILDABLE` exists to catch a new class before it can reach that path.
 *
 * WHOSE `method` and `url` a rebuilt failure carries is the caller's own. On the
 * default fingerprint the two are the same by construction, since both are
 * ingredients of the key. On a named pool they can differ, and the caller's own
 * is the answer that stays consistent: `options` on the rebuilt failure is this
 * caller's, so reading a different url off `error.url` than off
 * `error.options.url` would be the worse surprise (`CO63`).
 */
export function forThisCaller(
  failure: unknown,
  method: string,
  url: string,
  made: HttpErrorOptions,
): unknown {
  for (const Failure of REBUILDABLE) {
    if (failure instanceof Failure)
      return new Failure(method, url, { ...made, cause: failure.cause });
  }
  return failure;
}
